import { Box, Typography } from "@mui/material";
import { motion } from "motion/react";
import { tokens } from "../theme";
import CircularText from "../reactbits/CircularText";
import GradientText from "../reactbits/GradientText";
import BlurText from "../reactbits/BlurText";
import TextType from "../reactbits/TextType";
import RotatingText from "../reactbits/RotatingText";
import Orb from "../reactbits/Orb";
import SplashCursor from "../reactbits/SplashCursor";

export default function Hero() {
  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 3,
        pt: "120px",
        pb: "80px",
        // Removed overflow: "hidden" to prevent clipping
        background: tokens.ink,
      }}
    >
      {/* Orb Background with hover functionality - full coverage */}
      <Box
        sx={{
          position: "absolute",
          top: "-20%",
          left: "-20%",
          right: "-20%",
          bottom: "-20%",
          zIndex: 1,
          opacity: 0.5,
        }}
      >
        <Orb
          hue={0}
          hoverIntensity={0.3}
          rotateOnHover={true}
          forceHoverState={false}
          backgroundColor={tokens.ink}
        />
      </Box>

      {/* Splash Cursor Effect */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2,
          pointerEvents: "none",
        }}
      >
        <SplashCursor
          SIM_RESOLUTION={128}
          DYE_RESOLUTION={1440}
          DENSITY_DISSIPATION={3.5}
          VELOCITY_DISSIPATION={2}
          PRESSURE={0.1}
          CURL={3}
          SPLAT_RADIUS={0.2}
          SPLAT_FORCE={6000}
          COLOR_UPDATE_SPEED={10}
        />
      </Box>

      {/* Content */}
      <Box
        sx={{
          position: "relative",
          zIndex: 3,
          textAlign: "center",
          maxWidth: 900,
          mx: "auto",
        }}
      >
        <Box
          sx={{
            fontFamily: tokens.mono,
            fontSize: "0.72rem",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: tokens.paperDim,
            mb: 5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.25,
            "&::before, &::after": {
              content: '""',
              width: 26,
              height: "1px",
              bgcolor: tokens.paperFaint,
            },
          }}
        >
          LUMS Computer Vision &amp; Graphics Lab
        </Box>

        {/* Orb glow + circular rotating ring text + gradient wordmark */}
        <Box
          sx={{
            position: "relative",
            width: "min(350px, 90vw)",
            height: "min(350px, 90vw)",
            mx: "auto",
            mb: 1,
          }}
        >
          <motion.div
            style={{
              position: "absolute",
              inset: "14%",
              borderRadius: "50%",
              background:
                "radial-gradient(circle at 50% 45%, rgba(232,118,58,0.55), rgba(127,216,208,0.18) 55%, transparent 75%)",
              filter: "blur(22px)",
            }}
            animate={{ opacity: [0.55, 0.85, 0.55], scale: [1, 1.07, 1] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          />

          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularText
              text="EARLY WARNING SYSTEMS · FOR CLIMATE RESILIENCE · "
              size={300}
              radius={135}
              spinDuration={26}
              onHover="slowDown"
              letterColor={tokens.paperDim}
            />
          </Box>

          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <GradientText
              colors={[tokens.ember, tokens.paper, tokens.frost, tokens.ember]}
              animationSpeed={7}
              style={{
                fontFamily: tokens.display,
                fontWeight: 600,
                fontStyle: "italic",
                fontSize: "clamp(2.6rem, 8vw, 4.2rem)",
                letterSpacing: "0.01em",
              }}
            >
              EWAS
            </GradientText>
          </Box>
        </Box>

        <BlurText
          text="Watching the wild, before disaster strikes."
          delay={40}
          style={{
            fontFamily: tokens.display,
            fontWeight: 500,
            fontSize: "clamp(1.3rem, 3vw, 1.85rem)",
            margin: "30px auto 18px",
            maxWidth: 640,
            lineHeight: 1.35,
            color: tokens.paper,
            justifyContent: "center",
          }}
        />

        <Box
          sx={{
            maxWidth: 520,
            mx: "auto",
            color: tokens.paperDim,
            fontSize: "1.02rem",
            lineHeight: 1.6,
            minHeight: "1.6em",
          }}
        >
          <TextType
            text={[
              "AI-powered sensors for forests, mountains, and the people who live among them.",
            ]}
            typingSpeed={26}
            initialDelay={900}
            loop={false}
            showCursor
            cursorCharacter="|"
          />
        </Box>

        <Typography
          sx={{
            fontFamily: tokens.mono,
            fontSize: "0.85rem",
            letterSpacing: "0.04em",
            color: tokens.paperDim,
            my: 4,
          }}
        >
          Built to sense{" "}
          <RotatingText
            texts={["wildfires", "wildlife conflict", "climate risk"]}
            rotationInterval={2200}
            splitBy="characters"
            style={{
              padding: "0 10px",
              borderRadius: 6,
              background: "rgba(127,216,208,0.12)",
              color: tokens.frost,
            }}
          />{" "}
          — before it becomes a disaster.
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              fontFamily: tokens.mono,
              fontSize: "0.72rem",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              px: 2.5,
              py: 1.4,
              borderRadius: "999px",
              border: "1px solid rgba(238,236,227,0.25)",
              color: tokens.paperDim,
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <motion.span
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: tokens.ember,
                display: "inline-block",
              }}
              animate={{ opacity: [1, 0.25, 1] }}
              transition={{
                duration: 1.8,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            Coming soon
          </Box>

          <Box
            component="a"
            href="mailto:murtaza.taj@lums.edu.pk?subject=EWAS%20%E2%80%94%20Partnership%20Interest"
            sx={{
              fontWeight: 600,
              fontSize: "0.92rem",
              px: 3.25,
              py: 1.5,
              borderRadius: "999px",
              textDecoration: "none",
              bgcolor: tokens.paper,
              color: tokens.ink,
              transition: "transform .25s ease, box-shadow .25s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 12px 30px rgba(232,118,58,0.25)",
              },
            }}
          >
            Get in touch
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
